/** DreamHome Journey messaging DB — schema version 1 (local, swap-ready). */

export const SCHEMA_VERSION = 2;
export const SCHEMA_VERSION_KEY = "dh_schema_version";

export const ROLE_TYPES = ["broker", "agent", "buyer", "seller", "admin"];
export const USER_STATUSES = ["active", "inactive"];
export const TRANSACTION_STATUSES = ["open", "closed", "abandoned"];
export const THREAD_STATUSES = ["active", "archived"];
export const EXPORT_TYPES = ["broker", "agent", "client"];
export const ARCHIVE_TYPES = ["closed", "abandoned"];
export const NOTIFICATION_TYPES = ["message", "export", "archive", "inquiry", "system", "calendar"];

export const TABLES = {
  users: "users",
  transactions: "transactions",
  message_threads: "message_threads",
  messages: "messages",
  attachments: "attachments",
  exports: "exports",
  archive_records: "archive_records",
  notifications: "notifications",
  audit_logs: "audit_logs",
  beta_feedback: "beta_feedback",
};

export const TABLE_KEYS = {
  users: "dh_j_users",
  transactions: "dh_j_transactions",
  message_threads: "dh_j_message_threads",
  messages: "dh_j_messages",
  attachments: "dh_j_attachments",
  exports: "dh_j_exports",
  archive_records: "dh_j_archive_records",
  notifications: "dh_j_notifications",
  audit_logs: "dh_j_audit_logs",
  beta_feedback: "dh_j_beta_feedback",
};

export const DEMO_IDS = {
  broker: "broker-demo",
  agent: "agent-demo",
  agent2: "agent-demo-2",
  buyer: "buyer-demo",
  seller: "seller-demo",
  admin: "admin-dev",
  brokerBuyer: "buyer-broker-demo",
  txBroker: "tx-broker-own",
  threadBroker: "thread-broker-own",
  txBuyer: "tx-buyer-demo",
  txSeller: "tx-seller-demo",
  threadBuyer: "thread-buyer-demo",
  threadSeller: "thread-seller-demo",
};

export function nowIso() {
  return new Date().toISOString();
}

export function newId() {
  return crypto.randomUUID();
}

export function emptyCollections() {
  return {
    users: [],
    transactions: [],
    message_threads: [],
    messages: [],
    attachments: [],
    exports: [],
    archive_records: [],
    notifications: [],
    audit_logs: [],
    beta_feedback: [],
  };
}

export function createUser(input = {}) {
  const timestamp = nowIso();
  return {
    id: input.id || newId(),
    role_type: input.role_type,
    name: input.name || "",
    email: String(input.email || "").toLowerCase(),
    status: input.status || "active",
    brokerage_id: input.brokerage_id || null,
    email_report_opt_in: Boolean(input.email_report_opt_in),
    office_name: input.office_name || "",
    plan: input.plan || null,
    plan_status: input.plan_status || null,
    seat_tier: input.seat_tier || null,
    linked_agent_name: input.linked_agent_name || "",
    linked_broker_name: input.linked_broker_name || "",
    first_name: input.first_name || "",
    last_name: input.last_name || "",
    phone: input.phone || "",
    license_number: input.license_number || "",
    client_code: input.client_code || "",
    notes: input.notes || "",
    assigned_agent_id: input.assigned_agent_id || null,
    betaTester: Boolean(input.betaTester),
    betaAccepted: Boolean(input.betaAccepted),
    betaRole: input.betaRole || null,
    betaAcceptedAt: input.betaAcceptedAt || null,
    created_at: input.created_at || timestamp,
    updated_at: input.updated_at || timestamp,
  };
}

export function createTransaction(input = {}) {
  const timestamp = nowIso();
  return {
    id: input.id || newId(),
    broker_id: input.broker_id,
    agent_id: input.agent_id,
    client_id: input.client_id,
    client_role: input.client_role || "buyer",
    property_address: input.property_address || "",
    status: input.status || "open",
    created_at: input.created_at || timestamp,
    closed_at: input.closed_at || null,
  };
}

export function createThread(input = {}) {
  return {
    id: input.id || newId(),
    transaction_id: input.transaction_id,
    created_by_user_id: input.created_by_user_id,
    created_at: input.created_at || nowIso(),
    status: input.status || "active",
  };
}

export function createMessage(input = {}) {
  return {
    id: input.id || newId(),
    thread_id: input.thread_id,
    sender_id: input.sender_id,
    receiver_id: input.receiver_id,
    message_text: input.message_text || "",
    timestamp: input.timestamp || nowIso(),
    read_status: Boolean(input.read_status),
    has_attachments: Boolean(input.has_attachments),
    tag: input.tag || null,
  };
}

export function createBetaFeedback(input = {}) {
  return {
    id: input.id || newId(),
    user_id: input.user_id || null,
    message_id: input.message_id || null,
    thread_id: input.thread_id || null,
    tag: input.tag || "betaFeedback",
    message_text: input.message_text || "",
    from_name: input.from_name || "",
    from_email: input.from_email || "",
    from_role: input.from_role || "",
    created_at: input.created_at || nowIso(),
    status: input.status || "new",
    admin_notes: input.admin_notes || "",
  };
}

export function createAttachment(input = {}) {
  return {
    id: input.id || newId(),
    message_id: input.message_id,
    file_path: input.file_path || "",
    file_name: input.file_name || "",
    mime_type: input.mime_type || "application/octet-stream",
    uploaded_at: input.uploaded_at || nowIso(),
  };
}

export function createExport(input = {}) {
  return {
    id: input.id || newId(),
    transaction_id: input.transaction_id,
    generated_by_user_id: input.generated_by_user_id,
    export_type: input.export_type,
    file_path: input.file_path || "",
    created_at: input.created_at || nowIso(),
  };
}

export function createArchiveRecord(input = {}) {
  return {
    id: input.id || newId(),
    transaction_id: input.transaction_id,
    archived_at: input.archived_at || nowIso(),
    archive_type: input.archive_type,
    report_export_id: input.report_export_id || null,
  };
}

export function createNotification(input = {}) {
  return {
    id: input.id || newId(),
    user_id: input.user_id,
    type: input.type,
    payload: input.payload || {},
    is_read: Boolean(input.is_read),
    created_at: input.created_at || nowIso(),
  };
}

export function createAuditLog(input = {}) {
  return {
    id: input.id || newId(),
    user_id: input.user_id || null,
    action_type: input.action_type,
    transaction_id: input.transaction_id || null,
    timestamp: input.timestamp || nowIso(),
    metadata: input.metadata || {},
  };
}

export const FOREIGN_KEYS = {
  transactions: [
    { field: "broker_id", table: "users" },
    { field: "agent_id", table: "users" },
    { field: "client_id", table: "users" },
  ],
  message_threads: [
    { field: "transaction_id", table: "transactions" },
    { field: "created_by_user_id", table: "users" },
  ],
  messages: [
    { field: "thread_id", table: "message_threads" },
    { field: "sender_id", table: "users" },
    { field: "receiver_id", table: "users" },
  ],
  attachments: [{ field: "message_id", table: "messages" }],
  exports: [
    { field: "transaction_id", table: "transactions" },
    { field: "generated_by_user_id", table: "users" },
  ],
  archive_records: [{ field: "transaction_id", table: "transactions" }],
  notifications: [{ field: "user_id", table: "users" }],
};
